import { useCallback, useEffect, useRef, useState } from "react";
import { GoogleAuthProvider, reauthenticateWithPopup } from "firebase/auth";
import { auth } from "@/lib/firebaseClient";
import { setReauthHandler } from "@/lib/reauth";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

/**
 * Step-up re-authentication prompt (DRA-350).
 *
 * The API rejects high-blast-radius actions - granting or revoking
 * super-admin, deleting a business or agent, editing a systemPrompt - when the
 * token's `auth_time` is older than the step-up window. This is what turns that
 * rejection into a recoverable prompt instead of a dead end.
 *
 * Uses reauthenticateWithPopup rather than the redirect flow that AuthProvider
 * uses for production sign-in. A redirect would navigate away mid-action and
 * lose the pending request, which defeats the point; the popup is opened from a
 * user gesture, so it is not treated as an unsolicited pop-up.
 *
 * Firebase only refreshes `auth_time` on genuine re-authentication, which is
 * exactly why a background token refresh cannot satisfy this gate.
 */
export function ReauthDialog() {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resolveRef = useRef<((ok: boolean) => void) | null>(null);

  const settle = useCallback((ok: boolean) => {
    resolveRef.current?.(ok);
    resolveRef.current = null;
    setOpen(false);
    setBusy(false);
    setError(null);
  }, []);

  useEffect(() => {
    setReauthHandler(
      () =>
        new Promise<boolean>((resolve) => {
          resolveRef.current = resolve;
          setError(null);
          setOpen(true);
        })
    );
    return () => setReauthHandler(null);
  }, []);

  async function confirm() {
    const user = auth.currentUser;
    if (!user) {
      settle(false);
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await reauthenticateWithPopup(user, new GoogleAuthProvider());
      // Force a refresh so the retried request carries the new auth_time.
      // Without this the replay reuses the cached token and is rejected again.
      await user.getIdToken(true);
      settle(true);
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      setBusy(false);
      setError(
        message.includes("popup-closed-by-user") || message.includes("cancelled-popup")
          ? "That window closed before you finished. Try again."
          : message
      );
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Dismissing is a decline, not a silent no-op: the pending request has
        // to be told, or its caller waits forever.
        if (!next && !busy) settle(false);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Confirm it&apos;s you</DialogTitle>
          <DialogDescription>
            This action can affect every tenant, so it needs a fresh sign-in.
            You will not lose what you were doing - it continues once you
            confirm.
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p className="text-sm text-destructive" role="alert">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => settle(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={() => void confirm()} disabled={busy}>
            {busy ? "Confirming..." : "Confirm identity"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
